// UI
export const NotificationDuration = 6000;

// Requests
export const FetchTimeout = 6000;

// Rides
export const RideTypes = [
    'running',
    'hiking',
    'bike',
    'walk'
];

export const DefaultRideType = RideTypes[0];

export const ElevationRideTypes = [
    'hiking',
    'bike'
];

// Duration→distance planning (round-trip loops, multi-day stages) can't know a route's elevation
// before it exists. For elevation activities we bias the assumed speed below the flat-terrain figure
// (Computation.estimateDuration uses base × 2.5 on the flat) so generated loops lean a bit short and
// rarely overshoot the requested duration on the hilly terrain typical of hiking/biking.
export const HillyPlanningSpeedFactor = 1.7;

export const PaceRideTypes = [
    'running',
    'walk'
];

// Pace profiles scale the estimated duration (sport = faster, tourist = slower).
// Keep the order/values in sync with Constant::PACE_PROFILES (Ruby).
export const PaceProfiles = [
    'tourist',
    'standard',
    'sport'
];

export const DefaultPaceProfile = 'standard';

export const DurationEstimationModes = {
    CLIENT: 'client',
    SERVER: 'server'
};

export const DefaultDurationEstimationMode = DurationEstimationModes.CLIENT;

export const PaceProfileFactors = {
    tourist: 0.85,
    standard: 1.0,
    sport: 1.25
};

export const AverageSpeedByRideType = {
    running: 9,
    hiking: 2,
    bike: 16,
    walk: 4.5
};

// Extra time (seconds) per metre of positive elevation gain, added on top of the flat estimate for
// bike and walk (hiking uses the full slope-band model). walk = Naismith (1 h per 600 m = 6 s/m);
// bike is deliberately lighter. Must stay in sync with the server Constant::ASCENT_TIME_SECONDS_PER_METER.
export const AscentTimeSecondsPerMeter = {
    walk: 6,
    bike: 3
};

// Distance (metres of travelled distance) over which the elevation profile is smoothed before slopes
// are binned, so a local duration estimate matches what the server will compute on save. Must stay in
// sync with the server Constant::ELEVATION_SMOOTHING_SPAN_BY_RIDE_TYPE (Cartography::Elevation).
export const DefaultElevationSmoothingSpan = 75;
export const ElevationSmoothingSpanByRideType = {
    running: 100,
    hiking: 75,
    walk: 75,
    bike: 150
};

// The server runs a LOWESS (locally-linear) fit, we run a centered moving average — cheaper, but it
// cuts harder at equal width. Measured over relief wavelengths of 40 to 600 m, a window of 0.6 x the
// span reproduces the server's ascent retention to within 1 to 2 points for all three ride types.
export const ElevationSmoothingWindowFactor = 0.6;

export const PaywallSoftFunctionalities = [
    'map_max_route_computation_count',
    'collection_max_count'
];

export const paywallTier = (functionality) => (
    PaywallSoftFunctionalities.includes(functionality) ? 'soft' : 'hard'
);

export const HomeOnboardingSteps = ['type', 'draw', 'edit', 'data', 'features', 'save'];
export const HomeOnboardingEnabled = true;
export const HomeOnboardingStorageKey = 'home_onboarding';

export const RideLevels = [
    'level_undefined',
    'very_easy',
    'easy',
    'medium',
    'difficult',
    'very_difficult'
];

export const RideVisibilities = [
    'everyone',
    'only_me'
];

export const RouteComputationTypes = [
    'foot',
    'hike',
    // 'hike_fastest',
    'bike',
    'mtb',
    'car',
    // 'car_fastest'
];

// Pois
export const PlanDateTypes = [
    'unplanned',
    'week',
    'month',
    'year',
    'date',
    'completed'
];

// export const rideProcessingTimer = 1000;

// export const rideCountLimit = 500;

// Map
export const MapLayers = {
    MAP_PLANNER: 'map_planner',
    MAP_PLANNER_OUTDOOR: 'map_planner_outdoor',
    MAPBOX_SATELLITE: 'mapbox_satellite',
    IGN: 'ign',
    SWISS_TOPO: 'swiss_topo',
    OS_MAPS: 'os_maps',
    USGS_TOPO: 'usgs_topo',
    OPEN_TOPO_MAP: 'open_topo_map',
    OPEN_CYCLE_MAP: 'open_cycle_map',
    OPEN_STREET_MAP: 'open_street_map'
    // MAPBOX_OUTDOOR: 'mapbox_outdoor',
    // MAPBOX_STREET: 'mapbox_street',
};

export const MapLayerNames = {
    map_planner: 'MAP PLANNER',
    map_planner_outdoor: 'MAP PLANNER OUTDOOR',
    mapbox_outdoor: 'MAPBOX',
    mapbox_street: 'MAPBOX STREET',
    mapbox_satellite: 'MAPBOX SATELLITE',
    open_street_map: 'OSM',
    open_cycle_map: 'OPEN CYCLE MAP',
    swiss_topo: 'SWISSTOPO',
    os_maps: 'OS MAPS',
    usgs_topo: 'USGS TOPO',
    open_topo_map: 'OPEN TOPO MAP',
    ign: 'IGN'
};

// Same profiles as the mobile app
export const RideTypeToRouteType = {
    running: 'running',
    hiking: 'hike',
    bike: 'bike',
    walk: 'foot_basic',
};

export const RouteTypeToRideType = {
    foot: 'running',
    running: 'running',
    foot_basic: 'walk',
    hike: 'hiking',
    bike: 'bike',
    mtb: 'bike',
    car: 'walk'
};

// Default of the "prefer paths / avoid car roads" routing axis for an activity: on for hiking only.
export const preferPathsDefault = (rideType) => rideType === 'hiking';

// Default of the "waymarked trails" axis: on for hiking only, like the mobile app (defaultRoutingOptions).
export const waymarkedTrailsDefault = (rideType) => rideType === 'hiking';

// Resolve the effective GraphHopper profile from the base activity profile and the two safety axes:
// - safe: prefer paths / avoid car roads (default ON for hiking; opt-in for walk & running, both on foot_safe)
// - waymarked: stick to officially waymarked trails (hiking only)
// Only the self-hosted engine exposes these variants; Mapbox degrades to walking upstream.
export const resolveRoutingProfile = (baseRouteType, {safe = false, waymarked = false} = {}) => {
    if (baseRouteType === 'hike') {
        if (safe && waymarked) return 'hike_safe_waymarked';
        if (waymarked) return 'hike_waymarked';
        if (safe) return 'hike_safe';
        return 'hike';
    }

    // walk & running: waymarked stays hiking-only, so only the path-preference applies.
    if (['foot', 'foot_basic', 'running'].includes(baseRouteType)) {
        return safe ? 'foot_safe' : baseRouteType;
    }

    return baseRouteType;
};

export const MILE_TO_METERS = 1609.344;

export const resolveMaxDistanceMeters = (config, rideType, unit) => {
    const isMi = unit === 'mi';

    if (typeof config === 'number') {
        return isMi ? (config / 1000) * MILE_TO_METERS : config;
    }

    const activity = config?.[rideType] || config?.[DefaultRideType] || {};
    // Fall back to the highest configured limit for this unit if the activity sub-key is
    // missing, so a partial config still enforces *a* cap (never silently unlimited).
    const value = (isMi ? activity.mi : activity.km) ?? maxMaxDistanceValue(config, unit);

    if (value == null) {
        return undefined;
    }

    return isMi ? value * MILE_TO_METERS : value * 1000;
};

export const maxMaxDistanceValue = (config, unit) => {
    const isMi = unit === 'mi';

    if (typeof config === 'number') {
        const km = config / 1000;
        return isMi ? Math.round(km * 0.621371) : km;
    }

    const values = Object.values(config || {})
        .map((activity) => (isMi ? activity?.mi : activity?.km))
        .filter((value) => value != null);

    return values.length ? Math.max(...values) : undefined;
};

export const MapIsochroneDistances = [
    5000,
    10000,
    15000,
    20000
];

export const MapIsochroneDurations = [
    900,
    1800,
    3600,
    7200
];

// Products
export const Products = {
    EXPLORER: 'explorer',
    EXPLORER_DISCOVER: 'explorer-discover'
};

// Search
export const AutocompleteLimit = 8;
export const MaxSearchRate = 200;

// Autocomplete
export const AutocompleteSuggestionsLimit = 20;

// Metasearch
export const MetaSearchLimit = 20;

// Uploads
export const UploadMaxWidth = 2560;
export const UploadMaxHeight = 2560;
